import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useGameStore } from '@/stores/gameStore';
import { loadAiGameContent, validateQuestionCount, type AiGameContent } from '@/config/gameQuestions';
import FluffyCreatureAvatar from '@/components/FluffyCreatureAvatar';
import FluffyCreatureStyles from '@/components/FluffyCreatureStyles';
import HowToPlayModal from '@/components/HowToPlayModal';
import { audioManager } from '@/utils/audioManager';
import { getCharacterSound, GAME_SOUNDS } from '@/config/soundEffects';
import { CHARACTERS } from '@/constants/characters';
import { authService } from '@/services/auth';
import { auth } from '@/services/firebase/config';
import { signInAnonymously } from 'firebase/auth';
import { transformQuestionSetToGameData } from '@/utils/questionSetUtils';
import type { AiGameSession, QuestionSet } from '@/types';
import { useSafeNavigate } from '@/hooks/useSafeNavigate';
import { useGameSessionStore } from '@/stores/gameSessionStore';
import { clearStoredSessionData } from '@/utils/sessionRecovery';

const AiSetup = () => {
  const navigate = useSafeNavigate();
  const [searchParams] = useSearchParams();
  const [playerName, setPlayerName] = useState('Player');
  const [selectedCharacter, setSelectedCharacter] = useState<number | null>(null);
  const [aiPlayerCount, setAiPlayerCount] = useState(3); // Default 3 AIs
  const [showCountdown, setShowCountdown] = useState(false);
  const [countdown, setCountdown] = useState(3);
  const [aiGameContent, setAiGameContent] = useState<AiGameContent | null>(null);
  const [isLoadingQuestions, setIsLoadingQuestions] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isHowToPlayOpen, setIsHowToPlayOpen] = useState(false);
  const [aiGameSession, setAiGameSession] = useState<AiGameSession | null>(null);
  const [isLoadingSession, setIsLoadingSession] = useState(false);
  const [sessionError, setSessionError] = useState<string | null>(null);

  const characters = CHARACTERS;
  const aiGameCodeParam = searchParams.get('gameCode');
  const aiGameCode = aiGameCodeParam ? aiGameCodeParam.trim().toUpperCase() : null;
  const isAiPlayerCountLocked = Boolean(aiGameSession && !aiGameSession.allowStudentChoice);
  const aiGameExpirationLabel = aiGameSession
    ? aiGameSession.endDate.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric'
      })
    : null;

  const { initializeGame, setSelectedCharacter: storeSetSelectedCharacter, setAiPlayerCount: storeSetAiPlayerCount } = useGameStore();
  const resetSessionStore = useGameSessionStore(state => state.reset);

  const loadQuestionSetForSession = useCallback(async (session: AiGameSession): Promise<QuestionSet | null> => {
    if (session.questionSetSnapshot) {
      return session.questionSetSnapshot;
    }

    try {
      return await authService.getQuestionSet(session.questionSetId);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);

      if (message.includes('Missing or insufficient permissions')) {
        throw new Error(
          'This AI game code was created before question content was embedded for student access. Ask the teacher to recreate the AI game code.'
        );
      }

      throw error;
    }
  }, []);

  const ensureAnonymousAuth = useCallback(async () => {
    try {
      if (!auth.currentUser) {
        await signInAnonymously(auth);
      }
    } catch (error) {
      console.error('❌ Anonymous authentication failed for AI setup:', error);

      if (error instanceof Error) {
        if (error.message.includes('auth/admin-restricted-operation')) {
          throw new Error('Anonymous authentication is disabled. Please contact the teacher to enable guest access.');
        }

        if (error.message.includes('auth/network-request-failed')) {
          throw new Error('Network connection failed. Please check your internet connection.');
        }
      }

      throw new Error('Authentication failed. Please try again or contact support.');
    }
  }, []);

  useEffect(() => {
    let isMounted = true;

    if (!aiGameCode) {
      if (isMounted) {
        setAiGameSession(null);
        setAiGameContent(null);
        setSessionError(null);
        setIsLoadingSession(false);
        setAiPlayerCount(3);
      }
      return () => {
        isMounted = false;
      };
    }

    setAiGameSession(null);
    setAiGameContent(null);
    setSessionError(null);
    setIsLoadingSession(true);

    const loadSession = async () => {
      try {
        await ensureAnonymousAuth();

        const session = await authService.getAiGameSession(aiGameCode, true);
        if (!isMounted) return;

        if (!session) {
          setSessionError('Game not found. Please check the game code and try again.');
          return;
        }

        if (session.status === 'expired') {
          setSessionError('This game has expired. Please contact your teacher for a new code.');
          return;
        }

        if (session.status === 'cancelled') {
          setSessionError('This game has been cancelled. Please contact your teacher.');
          return;
        }

        const questionSet = await loadQuestionSetForSession(session);
        if (!isMounted) return;

        if (!questionSet) {
          setSessionError('Unable to load the question set for this AI game.');
          return;
        }

        const { questions, stages } = transformQuestionSetToGameData(questionSet);

        if (!validateQuestionCount(questions)) {
          setSessionError('This AI game has an invalid question configuration. Please contact your teacher.');
          return;
        }

        setAiGameSession(session);
        setAiGameContent({
          questionSetId: questionSet.id,
          questionSetName: questionSet.name ?? session.questionSetName,
          questions,
          stages
        });

        if (!session.allowStudentChoice && typeof session.fixedAiPlayerCount === 'number') {
          setAiPlayerCount(session.fixedAiPlayerCount);
        }
      } catch (error: unknown) {
        if (!isMounted) return;
        console.error('❌ Failed to load AI game session:', error);
        const message = error instanceof Error ? error.message : 'Failed to load AI game session. Please try again.';
        setSessionError(message);
      } finally {
        if (isMounted) {
          setIsLoadingSession(false);
        }
      }
    };

    loadSession();

    return () => {
      isMounted = false;
    };
  }, [aiGameCode, ensureAnonymousAuth, loadQuestionSetForSession]);

  // Countdown logic
  useEffect(() => {
    if (!showCountdown) {
      return;
    }

    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }

    // Countdown reached zero
    storeSetAiPlayerCount(aiPlayerCount);

    if (!aiGameContent) {
      console.error('❌ AiSetup: Countdown finished but AI game content is missing');
      setShowCountdown(false);
      setCountdown(3);
      return;
    }

    if (!validateQuestionCount(aiGameContent.questions)) {
      console.error('❌ AiSetup: Invalid AI question configuration');
      setShowCountdown(false);
      setCountdown(3);
      return;
    }

    const sessionIdentifier = aiGameSession?.id ?? 'ai-game-session';

    resetSessionStore();
    clearStoredSessionData();
    initializeGame(sessionIdentifier, aiGameContent.questions, {
      stages: aiGameContent.stages,
      questionSetId: aiGameContent.questionSetId,
      questionSetName: aiGameContent.questionSetName,
      mode: 'ai'
    });

    setShowCountdown(false);
    setCountdown(3);
    navigate('/question');
  }, [
    showCountdown,
    countdown,
    aiPlayerCount,
    aiGameContent,
    initializeGame,
    navigate,
    resetSessionStore,
    storeSetAiPlayerCount,
    aiGameSession
  ]);

  const canStartGame = (
    playerName.trim().length > 0 &&
    selectedCharacter !== null &&
    !sessionError &&
    (!aiGameSession || (!!aiGameContent && !isLoadingSession))
  );
  const isStartDisabled = !canStartGame || isLoadingQuestions;

  const handleStartGame = async () => {
    if (!canStartGame || isLoadingQuestions) {
      return;
    }

    if (aiGameSession && !aiGameContent) {
      setLoadError('Game content is still loading. Please wait a moment and try again.');
      return;
    }

    // Ensure audio is initialized on user interaction
    if (!audioManager.isReady()) {
      try {
        await audioManager.initialize();
        console.log('✅ Audio initialized via AI setup');
      } catch (error) {
        console.warn('⚠️ Audio initialization failed:', error);
      }
    }

    // Store selected character
    if (selectedCharacter !== null) {
      storeSetSelectedCharacter(selectedCharacter);
    }

    setIsLoadingQuestions(true);
    setLoadError(null);

    try {
      let content = aiGameContent;

      if (!content) {
        await ensureAnonymousAuth();

        const loadedContent = await loadAiGameContent();

        if (!validateQuestionCount(loadedContent.questions)) {
          throw new Error('AI question set is missing required questions');
        }

        setAiGameContent(loadedContent);
        content = loadedContent;
      }

      if (!content) {
        throw new Error('AI game content is not available yet. Please try again.');
      }

      setCountdown(3);
      setShowCountdown(true);
    } catch (error: unknown) {
      console.error('❌ Failed to load AI game content:', error);
      setLoadError(error instanceof Error ? error.message : 'Failed to load AI game content. Please try again.');
      if (!aiGameSession) {
        setAiGameContent(null);
      }
    } finally {
      setIsLoadingQuestions(false);
    }
  };

  const handleAiCountChange = (count: number) => {
    if (isAiPlayerCountLocked) {
      return;
    }

    // Ensure count stays within bounds (1-10)
    const clampedCount = Math.max(1, Math.min(10, count));
    setAiPlayerCount(clampedCount);
  };

  const handleCharacterSelect = async (characterId: number) => {
    setSelectedCharacter(characterId);

    // Ensure audio is initialized
    if (!audioManager.isReady()) {
      try {
        await audioManager.initialize();
      } catch (error) {
        console.warn('⚠️ Audio initialization failed:', error);
      }
    }

    // Play character selection sound
    const characterSound = getCharacterSound(characterId);
    if (characterSound && audioManager.isReady()) {
      audioManager.play(characterSound.id);
    }
  };

  useEffect(() => {
    if (!showCountdown || !audioManager.isReady()) {
      return;
    }

    if (countdown > 0) {
      // Restart tick sound so it syncs with each number
      audioManager.stop(GAME_SOUNDS.COUNTDOWN_TICK.id);
      audioManager.play(GAME_SOUNDS.COUNTDOWN_TICK.id);
    } else if (countdown === 0) {
      audioManager.stop(GAME_SOUNDS.COUNTDOWN_TICK.id);
      audioManager.play(GAME_SOUNDS.GAME_START.id);
    }
  }, [showCountdown, countdown]);

  useEffect(() => {
    if (!showCountdown && audioManager.isReady()) {
      audioManager.stop(GAME_SOUNDS.COUNTDOWN_TICK.id);
    }
  }, [showCountdown]);

  if (showCountdown) {
    return (
      <div style={{
        padding: '2rem',
        textAlign: 'center',
        height: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <div style={{
          backgroundColor: 'rgba(255,255,255,0.1)',
          padding: '3rem',
          borderRadius: '20px',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)'
        }}>
          <h1 style={{ fontSize: '3rem', marginBottom: '2rem' }}>🚀 Starting Game...</h1>
          <div style={{
            fontSize: '8rem',
            fontWeight: 'bold',
            color: '#FFD700',
            textShadow: '0 0 30px rgba(255,215,0,0.6)',
            animation: 'pulse 1s ease-in-out'
          }}>
            {countdown || '🎮'}
          </div>
          <p style={{ fontSize: '1.5rem', marginTop: '2rem', opacity: 0.9 }}>
            Preparing {aiPlayerCount} AI opponents...
          </p>
        </div>
        <style>{`
          @keyframes pulse {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.1); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div style={{
      padding: '2rem',
      textAlign: 'center',
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white'
    }}>
      <div style={{
        backgroundColor: 'rgba(255,255,255,0.1)',
        padding: '2rem',
        borderRadius: '20px',
        backdropFilter: 'blur(10px)',
        boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
        maxWidth: '800px',
        margin: '0 auto'
      }}>
        {/* Header */}
        <h1 style={{
          fontSize: '2.5rem',
          marginBottom: '1rem',
          background: 'linear-gradient(45deg, #FFD700, #FFA500)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent'
        }}>
          🤖 Play with AI
        </h1>
        <p style={{ fontSize: '1.1rem', marginBottom: '2rem', opacity: 0.9 }}>
          Configure your AI opponents and choose your character
        </p>

        {aiGameCode && (
          <div style={{
            backgroundColor: 'rgba(255,255,255,0.12)',
            padding: '1.5rem',
            borderRadius: '12px',
            marginBottom: '2rem',
            textAlign: 'left',
            border: '2px solid rgba(255,215,0,0.25)'
          }}>
            <h3 style={{ marginBottom: '1rem', color: '#FFD700' }}>Shared AI Game</h3>
            {isLoadingSession && (
              <p style={{ color: 'rgba(255,255,255,0.85)' }}>
                Loading game details for code <strong>{aiGameCode}</strong>...
              </p>
            )}
            {!isLoadingSession && sessionError && (
              <p style={{ color: '#FFB4B4', fontWeight: 'bold', margin: 0 }}>
                {sessionError}
              </p>
            )}
            {!isLoadingSession && !sessionError && aiGameSession && (
              <div style={{ display: 'grid', gap: '0.6rem', color: 'rgba(255,255,255,0.9)' }}>
                <p><strong>👩‍🏫 Teacher:</strong> {aiGameSession.teacherName}</p>
                <p><strong>📚 Question Set:</strong> {aiGameSession.questionSetName}</p>
                <p><strong>🔢 Code:</strong> {aiGameSession.gameCode}</p>
                {aiGameExpirationLabel && (
                  <p><strong>⏰ Expires:</strong> {aiGameExpirationLabel}</p>
                )}
                <p>
                  <strong>🤖 AI Opponents:</strong>{' '}
                  {aiGameSession.allowStudentChoice
                    ? 'You choose how many AI players join you.'
                    : `${aiGameSession.fixedAiPlayerCount ?? aiPlayerCount} opponents (locked by teacher)`}
                </p>
              </div>
            )}
          </div>
        )}

        {/* AI Player Count Selection */}
        <div style={{
          backgroundColor: 'rgba(255,255,255,0.1)',
          padding: '1.5rem',
          borderRadius: '12px',
          marginBottom: '2rem'
        }}>
          <h3 style={{ marginBottom: '1rem', color: '#FFD700' }}>Number of AI Opponents</h3>
          <input
            type="number"
            min="1"
            max="10"
            value={aiPlayerCount}
            onChange={(e) => handleAiCountChange(parseInt(e.target.value) || 1)}
            disabled={isAiPlayerCountLocked || isLoadingSession}
            style={{
              width: '100px',
              padding: '0.8rem',
              fontSize: '1.2rem',
              fontWeight: 'bold',
              textAlign: 'center',
              borderRadius: '8px',
              border: '2px solid rgba(255,255,255,0.3)',
              backgroundColor: (isAiPlayerCountLocked || isLoadingSession) ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.1)',
              color: 'white',
              outline: 'none',
              cursor: (isAiPlayerCountLocked || isLoadingSession) ? 'not-allowed' : 'text',
              opacity: (isAiPlayerCountLocked || isLoadingSession) ? 0.6 : 1
            }}
          />
          <p style={{ fontSize: '0.9rem', marginTop: '0.5rem', opacity: 0.8 }}>
            {isLoadingSession
              ? 'Loading game settings...'
              : isAiPlayerCountLocked
                ? 'Your teacher has set the number of AI opponents for this game.'
                : 'Choose 1-10 AI players'}
          </p>
        </div>

        {/* Player Name Input */}
        <div style={{
          backgroundColor: 'rgba(255,255,255,0.1)',
          padding: '1.5rem',
          borderRadius: '12px',
          marginBottom: '2rem'
        }}>
          <h3 style={{ marginBottom: '1rem', color: '#FFD700' }}>Your Name</h3>
          <input
            type="text"
            value={playerName}
            onChange={(e) => setPlayerName(e.target.value)}
            placeholder="Enter your name"
            style={{
              width: '100%',
              maxWidth: '300px',
              padding: '0.8rem',
              fontSize: '1.1rem',
              borderRadius: '8px',
              border: '2px solid rgba(255,255,255,0.3)',
              backgroundColor: 'rgba(255,255,255,0.1)',
              color: 'white',
              textAlign: 'center'
            }}
          />
        </div>

        {/* Character Selection - Copied exactly from PlayerSetup */}
        <div style={{
          backgroundColor: 'rgba(255,255,255,0.1)',
          padding: '1.5rem',
          borderRadius: '12px',
          marginBottom: '2rem'
        }}>
          <label style={{
            fontSize: '1.2rem',
            marginBottom: '1rem',
            color: '#FFD700',
            fontWeight: 'bold'
          }}>
            Choose Your Creature:
          </label>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '1rem',
            maxWidth: '400px',
            margin: '0 auto'
          }}>
            {characters.map((character) => (
              <div
                key={character.id}
                onClick={() => handleCharacterSelect(character.id)}
                style={{
                  cursor: 'pointer',
                  padding: '1rem',
                  borderRadius: '16px',
                  border: selectedCharacter === character.id ? '4px solid #FFD700' : '2px solid rgba(255,255,255,0.3)',
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  transition: 'all 0.3s ease',
                  transform: selectedCharacter === character.id ? 'scale(1.1)' : 'scale(1)',
                  boxShadow: selectedCharacter === character.id
                    ? '0 0 20px rgba(255,215,0,0.5)'
                    : '0 2px 8px rgba(0,0,0,0.2)'
                }}
              >
                <FluffyCreatureAvatar
                  primaryColor={character.primaryColor}
                  secondaryColor={character.secondaryColor}
                  accentColor={character.accentColor}
                />
                <p style={{
                  fontSize: '0.8rem',
                  color: 'white',
                  textAlign: 'center',
                  margin: '0.5rem 0 0 0',
                  fontWeight: 'bold'
                }}>
                  {character.name}
                </p>
              </div>
            ))}
          </div>
        </div>

        <button
          onClick={() => setIsHowToPlayOpen(true)}
          style={{
            padding: '0.9rem 1.7rem',
            fontSize: '1.05rem',
            backgroundColor: 'rgba(255,255,255,0.2)',
            color: '#FFFFFF',
            border: '2px solid rgba(255,255,255,0.35)',
            borderRadius: '999px',
            cursor: 'pointer',
            fontWeight: 'bold',
            marginBottom: '1.5rem',
            boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
            transition: 'all 0.3s ease'
          }}
        >
          🧭 How To Play
        </button>

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
          <button
            onClick={() => navigate('/lobby')}
            style={{
              padding: '1rem 2rem',
              fontSize: '1.1rem',
              backgroundColor: 'rgba(255,255,255,0.2)',
              color: 'white',
              border: '2px solid rgba(255,255,255,0.3)',
              borderRadius: '12px',
              cursor: 'pointer',
              fontWeight: 'bold'
            }}
          >
            ← Back to Lobby
          </button>

          <button
            onClick={handleStartGame}
            disabled={isStartDisabled}
            style={{
              padding: '1rem 2rem',
              fontSize: '1.1rem',
              backgroundColor: isStartDisabled ? '#666' : '#4CAF50',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              cursor: isStartDisabled ? 'not-allowed' : 'pointer',
              fontWeight: 'bold',
              boxShadow: isStartDisabled ? 'none' : '0 4px 16px rgba(76,175,80,0.4)',
              opacity: isStartDisabled ? 0.6 : 1
            }}
          >
            {isLoadingQuestions ? '⏳ Loading Questions...' : '🚀 Start Game'}
          </button>
        </div>

        {loadError && (
          <div style={{
            marginTop: '1rem',
            color: '#FFB4B4',
            fontWeight: 'bold'
          }}>
            {loadError}
          </div>
        )}

        <HowToPlayModal
          isOpen={isHowToPlayOpen}
          onClose={() => setIsHowToPlayOpen(false)}
        />

        <FluffyCreatureStyles />
      </div>
    </div>
  );
};

export default AiSetup;
